import { computed, Injectable, OnDestroy, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { APP_BUILD_INFO } from '../version/app-build-info';
import {
  AppPlatform,
  AppReleaseManifest,
  isLocalDesktopDevelopmentRuntime,
  resolveAppUpdateState,
  resolveNativeCall
} from './app-update.helpers';
import { isHeyJivuDesktop, isHeyJivuMobileApp, type NativeAppUpdateProgress } from '../constants/runtime.constants';

type AppUpdatePhase = 'idle' | 'downloading' | 'ready' | 'installing' | 'permission' | 'failed';

@Injectable({ providedIn: 'root' })
export class AppUpdateService implements OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly manifest = signal<AppReleaseManifest | null>(null);
  private readonly platform = signal<AppPlatform>(detectAppPlatform());
  private readonly localDesktopDevelopment = typeof window !== 'undefined'
    && isLocalDesktopDevelopmentRuntime(isHeyJivuDesktop(), window.location.hostname);
  private readonly pollIntervalMs = 20 * 60 * 1000;
  private pollTimer: number | undefined;
  private nativeUpdateStarted = false;
  private desktopProgressCleanup?: () => void;
  private mobileProgressCleanup?: () => Promise<void> | void;

  readonly state = computed(() =>
    resolveAppUpdateState(APP_BUILD_INFO, this.manifest(), this.platform(), environment.apiUrl));
  readonly currentVersion = computed(() => this.state().currentVersion);
  readonly latestVersion = computed(() => this.state().latestVersion);
  readonly hasUpdate = computed(() => !this.localDesktopDevelopment && this.state().hasUpdate);
  readonly requiresUpdate = computed(() => !this.localDesktopDevelopment && this.state().requiresUpdate);
  readonly updateMode = computed(() => this.state().updateMode);
  readonly showVersion = computed(() => APP_BUILD_INFO.channel === 'production');
  readonly updatePhase = signal<AppUpdatePhase>('idle');
  readonly downloadProgress = signal(0);
  readonly updateMessage = signal('');
  readonly updateError = signal('');
  readonly isUpdating = computed(() => ['downloading', 'installing'].includes(this.updatePhase()));
  readonly updateActionLabel = computed(() => {
    switch (this.updatePhase()) {
      case 'downloading': return `Downloading ${this.downloadProgress()}%`;
      case 'ready': return 'Install update';
      case 'installing': return 'Installing update';
      case 'permission': return 'Continue installation';
      case 'failed': return 'Retry update';
      default: return 'Download update';
    }
  });

  constructor() {
    if (this.localDesktopDevelopment) {
      return;
    }

    this.bindNativeProgress();
    this.refresh();

    if (typeof window !== 'undefined') {
      this.pollTimer = window.setInterval(() => this.refresh(), this.pollIntervalMs);
    }
  }

  ngOnDestroy(): void {
    if (this.pollTimer !== undefined && typeof window !== 'undefined') {
      window.clearInterval(this.pollTimer);
      this.pollTimer = undefined;
    }
    this.desktopProgressCleanup?.();
    void this.mobileProgressCleanup?.();
  }

  refresh(): void {
    const manifestUrl = `${trimTrailingSlash(environment.apiUrl)}/downloads/manifest`;
    this.http.get<AppReleaseManifest>(manifestUrl).pipe(
      catchError(() => of(null))
    ).subscribe((manifest) => {
      this.manifest.set(manifest);
    });
  }

  applyUpdate(): void {
    const state = this.state();
    if (!this.hasUpdate() || typeof window === 'undefined') {
      return;
    }

    if (state.updateMode === 'download' && state.downloadUrl) {
      if (this.startNativeUpdate()) {
        return;
      }
      window.location.href = state.downloadUrl;
      return;
    }

    window.location.reload();
  }

  startAutomaticUpdate(): void {
    if (!this.requiresUpdate() || this.nativeUpdateStarted) {
      return;
    }

    this.startNativeUpdate();
  }

  private startNativeUpdate(): boolean {
    if (typeof window === 'undefined' || this.nativeUpdateStarted) {
      return this.nativeUpdateStarted;
    }

    const state = this.state();
    if (!state.downloadUrl) {
      return false;
    }

    const payload = {
      downloadUrl: state.downloadUrl,
      sha256: state.sha256,
      version: state.latestVersion
    };
    const desktopUpdater = window.heyjivuDesktop?.appUpdate;
    const mobileUpdater = window.Capacitor?.Plugins?.JivuUpdate;
    if (!desktopUpdater && !mobileUpdater) {
      return false;
    }

    const continuingInstall = this.updatePhase() === 'ready' || this.updatePhase() === 'permission';
    this.nativeUpdateStarted = true;
    this.updatePhase.set(continuingInstall ? 'installing' : 'downloading');
    if (!continuingInstall) {
      this.downloadProgress.set(0);
    }
    this.updateMessage.set(continuingInstall
      ? 'Preparing the verified update for installation...'
      : `Downloading HeyJivu ${state.latestVersion}...`);
    this.updateError.set('');

    const updatePromise = desktopUpdater
      ? desktopUpdater.downloadAndInstall(payload)
      : mobileUpdater!.downloadAndInstall(payload);
    void resolveNativeCall(updatePromise).then((result) => {
      if (result?.started === false) {
        throw new Error(result.error || 'Unable to start the app update.');
      }
    }).catch((error) => {
      this.handleNativeProgress({
        phase: 'failed',
        error: error instanceof Error ? error.message : 'Unable to start the app update.'
      });
    });
    return true;
  }

  private bindNativeProgress(): void {
    if (typeof window === 'undefined') {
      return;
    }

    this.desktopProgressCleanup = window.heyjivuDesktop?.appUpdate?.onProgress?.(
      (payload) => this.handleNativeProgress(payload));

    const addMobileListener = window.Capacitor?.Plugins?.JivuUpdate?.addListener;
    if (addMobileListener) {
      const listenerHandle = addMobileListener(
        'updateProgress',
        (payload) => this.handleNativeProgress(payload));
      void resolveNativeCall(listenerHandle)
        .then((handle) => {
          this.mobileProgressCleanup = () => handle.remove();
        })
        .catch(() => undefined);
    }
  }

  private handleNativeProgress(payload: NativeAppUpdateProgress): void {
    const phase = payload.phase;
    if (phase) {
      this.updatePhase.set(phase);
    }

    if (typeof payload.percent === 'number') {
      this.downloadProgress.set(Math.max(0, Math.min(100, Math.round(payload.percent))));
    }

    if (payload.message) {
      this.updateMessage.set(payload.message);
    }

    if (phase === 'ready' || phase === 'permission' || phase === 'failed') {
      this.nativeUpdateStarted = false;
    }

    if (phase === 'failed') {
      this.updateError.set(payload.error || payload.message || 'The update could not be installed.');
    }
  }
}

function detectAppPlatform(): AppPlatform {
  if (typeof window === 'undefined') {
    return 'web';
  }

  if (isHeyJivuDesktop()) {
    return 'windows';
  }

  if (isHeyJivuMobileApp()) {
    return 'android';
  }

  return 'web';
}

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '');
}
